<template>
  <div id="search-member">
    <div class="search-container">
      <img src="@shared/assets/img/general/svg/magnify.svg" alt="search" class="img-size-24" />
      <input
        ref="searchRef"
        v-model="search"
        :placeholder="`Search ${activeSearch} (min 3 characters)...`"
        class="search-input"
      />
      <img
        v-if="loading"
        src="@shared/assets/img/general/loading.svg"
        alt="loading"
        class="img-size-24"
      />
    </div>

    <div class="grid-column ai-c jc-s padding-default">
      <SACTip :tip="hintText" class="margin-right-16" />

      <button
        type="button"
        :class="['tab-btn', { active: activeSearch === 'member' }]"
        @click="toggleSearch('member')"
      >
        Member
      </button>

      <button
        type="button"
        :class="['tab-btn', { active: activeSearch === 'tech' }]"
        @click="toggleSearch('tech')"
      >
        Tech
      </button>

      <div class="grid-column ai-c jc-s margin-left-32">
        <SACCheck field="selectedParams" :checked="!isActive" :change="toggleActive" />
        <span class="sac__font-14 n60 med">Include inactive</span>
      </div>
    </div>

    <div v-if="activeSearch === 'member'" class="grid-column ai-s jc-s gap-16 padding-default">
      <SACRadio
        label="Name"
        field="customerName"
        :checked="focusSearch.customerName"
        :change="changeSearchParam"
      />
      <SACRadio
        label="Email"
        field="email"
        :checked="focusSearch.email"
        :change="changeSearchParam"
      />
      <SACRadio
        label="Address"
        field="address"
        :checked="focusSearch.address"
        :change="changeSearchParam"
      />
      <SACRadio
        label="Plan"
        field="planName"
        :checked="focusSearch.planName"
        :change="changeSearchParam"
      />
    </div>

    <div v-else-if="activeSearch === 'tech'" class="grid-column ai-s jc-s gap-16 padding-default">
      <SACRadio
        v-if="showPartnerFilter"
        label="Partner"
        field="corpName"
        :checked="focusSearch.corpName"
        :change="changeSearchParam"
      />
    </div>

    <div v-if="search.length > 2" class="scrollable-container">
      <div class="grid-column ai-c jc-b">
        <span class="sac__font-12 n90 semi-bold">Search result..</span>
        <span v-if="loading" class="sac__font-10 n70 semi-bold">...</span>
        <span v-else-if="uniqSearchList.length === 0" class="sac__font-12 semi-bold">
          No results found..
        </span>
        <div v-else class="sac__font-14 semi-bold sac-tag f-primary p-4 rounded-8">
          {{ uniqSearchList.length }}
        </div>
      </div>

      <CustomerResultList
        v-if="activeSearch === 'member'"
        :search-list="uniqSearchList"
        :loading="loading"
      />
      <TechResultList
        v-else-if="activeSearch === 'tech'"
        :search-list="uniqSearchList"
        :loading="loading"
      />
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapGetters, mapState } from 'vuex';
import SACRadio from '@shared/components/sac/SACRadio.vue';
import SACTip from '@shared/components/sac/SACTip.vue';
import SACCheck from '@shared/components/sac/SACCheck.vue';
import CustomerResultList from '@shared/components/navbar/modal/CustomerResultList.vue';
import TechResultList from '@shared/components/navbar/modal/TechResultList.vue';

export default {
  name: 'SearchMember',
  components: {
    SACCheck,
    TechResultList,
    CustomerResultList,
    SACTip,
    SACRadio,
  },
  data() {
    return {
      search: '',
      loading: false,
      uniqSearchList: [],
      activeSearch: 'member',
      focusSearch: {
        corpName: false,
        techId: false,
        defaultSearch: true,
        customerName: false,
        phone: false,
        email: false,
        address: false,
        uid: false,
        planName: false,
        customerId: false,
      },
      isActive: true,
      searchCounter: 0,
      hintText: '',
      memberTipText:
        '1. Entering a 7-digit number automatically searches by Member ID (e.g., 3894444).\n' +
        '2. Entering a 10-digit or longer number automatically searches by phone number (e.g., 1234567890 or 123-456-7890).\n' +
        '3. UID search example: SMARTACHUB1E2X111111',
      techTipText: 'Entering a 6-digit number automatically searches by Technician ID',
    };
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.admin.current,
    }),
    ...mapGetters(['isSmacAdmin']),
    showPartnerFilter() {
      return this.isSmacAdmin || this.currentUser.corporation.parent;
    },
  },
  watch: {
    search(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.initialLoad();
      }
    },
  },

  created() {
    this.debouncedSearchMember = _.debounce(() => {
      this.searchMember();
    }, 500);
  },
  mounted() {
    this.toggleHint();
    if (this.$refs?.searchRef) {
      this.$refs.searchRef.focus();
    }
  },
  methods: {
    toggleActive() {
      this.isActive = !this.isActive;
      this.initialLoad();
    },
    toggleHint() {
      this.hintText = this.activeSearch === 'member' ? this.memberTipText : this.techTipText;
    },
    initialLoad() {
      this.toggleHint();
      this.uniqSearchList = [];
      this.searchCounter++;
      if (this.search.length > 2) {
        this.debouncedSearchMember();
      } else {
        this.debouncedSearchMember.cancel();
        this.loading = false;
      }
    },
    toggleSearch(value) {
      if (this.activeSearch === value) return;
      this.resetFocusSearch();
      this.activeSearch = value;
      this.initialLoad();
    },
    makeSearchQuery() {
      const query = {};

      if (!this.search || this.search.length <= 2) {
        return query;
      }

      const searchTerm = this.search.trim();
      const isNumeric = !isNaN(this.search);

      if (this.activeSearch === 'tech' && this.search.length === 6 && isNumeric) {
        query.techId = this.search;
      } else if (
        this.activeSearch === 'member' &&
        this.search.length === 20 &&
        this.search.toLowerCase().includes('smartachub')
      ) {
        query.uid = this.search;
      } else if (this.activeSearch === 'member' && this.search.length === 7 && isNumeric) {
        query.customerId = this.search;
      } else if (this.isValidPhone(this.search)) {
        query.phone = this.search.replaceAll('-', '');
      } else if (Object.keys(this.focusSearch).length) {
        Object.keys(this.focusSearch).forEach((key) => {
          if (this.focusSearch[key]) {
            query[key] = searchTerm;
          }
        });
      }

      const hasActiveField = Object.keys(query).some((key) => !!query[key]);
      if (!hasActiveField) {
        query.defaultSearch = searchTerm;
      }

      if (!this.isSmacAdmin) {
        const { corporation } = this.currentUser;
        query.corpId = corporation.parent ? corporation.id * -1 : corporation.id;
      }

      if (this.isActive) {
        query.active = true;
      }

      return query;
    },

    parseSearchResult(list) {
      const seen = new Set();
      const newList = [];

      if (Array.isArray(list) && list.length) {
        list.forEach((item) => {
          const { customerId, uid, id: techId } = item;

          if (techId) {
            if (!seen.has(techId)) {
              seen.add(techId);
              newList.push(item);
            }
          } else {
            const key = `${customerId}-${uid}`;
            if (!seen.has(key)) {
              seen.add(key);
              newList.push(item);
            }
          }
        });
      }

      this.uniqSearchList = newList;
    },

    changeSearchParam(field) {
      this.resetFocusSearch();
      this.focusSearch[field] = !this.focusSearch[field];
      this.initialLoad();
    },

    resetFocusSearch() {
      Object.keys(this.focusSearch).forEach((key) => {
        this.focusSearch[key] = false;
      });
    },

    isValidPhone(value) {
      if (!value || value.length < 10) {
        return false;
      }
      const scrubPhone = value.replaceAll('-', '');
      if (scrubPhone.length !== 10) {
        return false;
      }
      return !isNaN(scrubPhone);
    },

    async searchMember() {
      const query = this.makeSearchQuery();

      if (!Object.keys(query).length) {
        return;
      }

      const requestId = ++this.searchCounter;
      this.loading = true;

      try {
        let list = [];
        if (this.activeSearch === 'member') {
          list = await this.$store.dispatch('searchCustomerList', query);
        } else if (this.activeSearch === 'tech') {
          list = await this.$store.dispatch('searchTechnicianList', query);
        }
        if (requestId === this.searchCounter) {
          this.parseSearchResult(list || []);
        }
      } finally {
        if (requestId === this.searchCounter) {
          this.loading = false;
        }
      }
    },

  },
};
</script>

<style scoped lang="scss">
#search-member {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;

  .search-container {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 1rem;
    padding: 4px 1rem;
    align-items: center;
    background-color: $N5;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;

    .search-input {
      background-color: $N5;
      padding: 16px;
      font-size: 14px;
    }
  }

  .tab-btn {
    padding: 4px 10px;
    font-size: 14px;
    font-weight: 600;
    color: $NBLUE100;
    text-decoration: underline;

    &.active {
      border-radius: 6px;
      background-color: $NBLUE100;
      color: $WHITE;
      text-decoration: none;
    }
  }

  .padding-default {
    padding: 8px 1rem;
  }

  .scrollable-container {
    padding: 8px 1rem;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
}
</style>
